import { useEffect, useMemo, useState } from 'react'
import { useThree } from '@react-three/fiber'
import type { Mesh } from 'three'
import { useMaquete } from '../componentes/contextoMaquete'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { componenteDidatico } from '../../dominio/didatico/catalogo'
import { useContorno } from '../ambiente/registroPosProcessamento'
import { calcularPosicaoRotulo, coletarMeshesDaPeca } from './coletarMeshesDaPeca'

const MODOS_ANATOMIA = new Set([
  'externo',
  'estrutura',
  'srs',
  'interior',
  'mecanica',
  'energia',
])

/**
 * Destaca a peça selecionada no módulo de Anatomia Veicular através de contorno
 * de alto contraste e rótulo 3D com o nome do componente. O contorno é desenhado
 * pelo `PosProcessamento`; aqui só se publicam as malhas no canal `selecao`.
 *
 * Respeita a visibilidade das camadas e a hierarquia semântica: selecionar uma
 * dobradiça não destaca a porta inteira. Desativa o efeito fora da anatomia.
 */
export function DestaqueSelecaoAnatomia() {
  const { selecionada, modo, perfil, estados, camadas, explodido } = useMaquete()
  const scene = useThree((state) => state.scene)
  const [meshes, setMeshes] = useState<Mesh[]>([])

  const ativa = Boolean(selecionada && MODOS_ANATOMIA.has(modo))

  useEffect(() => {
    if (!ativa || !selecionada) {
      setMeshes([])
      return
    }

    // Coleta meshes imediatamente no ciclo de commit
    const coletadas = coletarMeshesDaPeca(scene, selecionada)
    setMeshes(coletadas)

    // Reforça caso geometrias assíncronas ou nós recém-montados precisem de um tick
    const timer = setTimeout(() => {
      const atualizadas = coletarMeshesDaPeca(scene, selecionada)
      setMeshes(atualizadas)
    }, 50)

    return () => {
      clearTimeout(timer)
      setMeshes([])
    }
  }, [ativa, selecionada, modo, perfil, estados, camadas, explodido, scene])

  useContorno('selecao', meshes)

  const posicaoRotulo = useMemo(() => calcularPosicaoRotulo(meshes), [meshes])
  const componente = selecionada ? componenteDidatico(selecionada) : null

  if (!ativa || meshes.length === 0 || !posicaoRotulo) return null

  return (
    <Rotulo3D
      posicao={posicaoRotulo}
      texto={componente?.nome ?? selecionada!}
      variante="estrutura"
    />
  )
}
